import React from "react";
import { motion } from "framer-motion";
import { UserPlus, Users, CalendarClock, ClipboardCheck, Award, Target, Hourglass, Layers, UserX } from "lucide-react";
import { CountUp, TONE, Skeleton } from "./primitives";

const RANGE_SUFFIX = { this_week: "This Week", this_month: "This Month", qtd: "QTD", this_year: "YTD", last_12: "Last 12 Mo", all: "All Time" };

// Shared recruiting metric definitions (same engine as the Activity Report and the drill-down lists).
const PERIOD = [
  { key: "new_leads", label: "New Leads", icon: UserPlus, tone: "blue", help: "First lead date in period" },
  { key: "interviews", label: "Interviews", icon: CalendarClock, tone: "purple", help: "Interview events in period" },
  { key: "career_conversions", label: "Career Conversions", icon: Award, tone: "green", help: "Actual Career date in period" },
  { key: "forecast_conversions", label: "Forecast Conversions", icon: Target, tone: "amber", help: "Target Career date — forecast, not actual" },
];
const CURRENT = [
  { key: "active_people", label: "Active People", icon: Users, tone: "blue", help: "Unique people now" },
  { key: "active_leads", label: "Active Leads", icon: Layers, tone: "blue", help: "Current lead stage" },
  { key: "pending", label: "Pending Agents", icon: Hourglass, tone: "purple", help: "Active now" },
  { key: "reserve", label: "Reserve Agents", icon: ClipboardCheck, tone: "green", help: "Active now, any Reserve date" },
];
const TINT = {
  blue: "bg-sky-50/70 border-sky-100 hover:border-sky-300", purple: "bg-violet-50/70 border-violet-100 hover:border-violet-300",
  green: "bg-emerald-50/70 border-emerald-100 hover:border-emerald-300", amber: "bg-amber-50/80 border-amber-200 hover:border-amber-300",
};

function Card({ k, i, value, label, sub, onClick }) {
  return (
    <motion.button data-testid={`kpi-${k.key}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}
      onClick={onClick} className={`group text-left rounded-xl border p-4 shadow-sm transition-[box-shadow,transform] duration-200 hover:shadow-md hover:-translate-y-0.5 ${TINT[k.tone]}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${TONE[k.tone]}`}><k.icon className="w-4 h-4" /></div>
      <p className="text-[26px] font-bold text-slate-900 leading-none" data-testid={`kpi-val-${k.key}`}><CountUp value={value} /></p>
      <p className="text-xs font-semibold text-slate-700 mt-2">{label}</p>
      <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{sub || k.help}</p>
    </motion.button>
  );
}

export function KpiCards({ metrics, range = "this_year", loading, onKpiDrill }) {
  const suffix = RANGE_SUFFIX[range] || "";
  if (loading && !metrics) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-row">{[...PERIOD, ...CURRENT].map((k) => <Skeleton key={k.key} h="h-28" />)}</div>;
  const c = metrics?.counts || {};
  const val = (k) => c[k] ?? 0;
  const sub = (k) => (k === "interviews" ? `${c.interviews_completed || 0} done · ${c.interviews_scheduled || 0} sched · ${c.interviews_cancelled || 0} canc · ${c.interviews_no_show || 0} no-show`
    : k === "active_people" ? `${c.active_opportunities || 0} opportunities (counted separately)` : null);
  const metricFor = (k) => k;
  return (
    <div className="space-y-2" data-testid="kpi-row">
      <div className="flex flex-wrap items-baseline gap-2 text-[11px] text-slate-500">
        <span className="font-semibold uppercase tracking-wide">During the period</span>
        {metrics?.scope_label && <span data-testid="kpi-scope">· {metrics.scope_label}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {PERIOD.map((k, i) => <Card key={k.key} k={k} i={i} value={val(k.key)} sub={sub(k.key)} label={`${k.label}${suffix ? ` ${suffix}` : ""}`} onClick={() => onKpiDrill?.(metricFor(k.key))} />)}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 pt-1">Current pipeline (now)</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CURRENT.map((k, i) => <Card key={k.key} k={k} i={i + 4} value={val(k.key)} sub={sub(k.key)} label={k.label} onClick={() => onKpiDrill?.(k.key)} />)}
      </div>
      {metrics?.breakdown?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="kpi-breakdown">
          <p className="text-xs font-semibold text-slate-700 mb-2">Program and stage (current, unique people)</p>
          <div className="flex flex-wrap gap-1.5">{metrics.breakdown.map((b) => (
            <button key={b.key} onClick={() => onKpiDrill?.(b.key)} data-testid={`kpi-breakdown-${b.program}-${b.stage}`.replace(/\s+/g, "-")}
              className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:border-brand/50 hover:text-brand transition-colors">
              {b.program} · {b.stage} <b className="text-slate-900">{b.count}</b></button>))}</div>
        </div>
      )}
      <TerminatedYtd count={val("terminated_ytd")} reasons={metrics?.terminated_reasons || []} onClick={() => onKpiDrill?.("terminated_ytd")} />
    </div>
  );
}

function TerminatedYtd({ count, reasons, onClick }) {
  return (
    <button onClick={onClick} data-testid="kpi-terminated_ytd"
      className="w-full text-left rounded-xl border border-rose-100 bg-rose-50/60 p-3 hover:border-rose-300 transition-colors flex flex-wrap items-center gap-3">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-rose-100 text-rose-700"><UserX className="w-4 h-4" /></span>
      <span>
        <span className="text-xl font-bold text-slate-900 mr-2" data-testid="kpi-val-terminated_ytd">{count}</span>
        <span className="text-xs font-semibold text-slate-700">Terminated Candidates YTD</span>
        <span className="block text-[11px] text-slate-500">Always year-to-date · excluded from active counts</span>
      </span>
      <span className="flex flex-wrap gap-1.5 ml-auto" data-testid="terminated-reasons">
        {reasons.length === 0 ? <span className="text-xs text-slate-400">No terminations this year</span> : reasons.map((r) => (
          <span key={r.reason} className="rounded-full border border-rose-200 bg-white px-2.5 py-0.5 text-xs text-slate-600"
            data-testid={`terminated-reason-${r.reason.replace(/\s+/g, "-")}`}>{r.reason} <b className="text-slate-900">{r.count}</b></span>))}
      </span>
    </button>
  );
}
